import debounce from 'hs-lodash/debounce';
import { useState, useEffect, useCallback } from 'react';
import { saveDraft } from '../local-storage/saveDraft';
import { makeDraftKeyFromId } from '../operators/makeDraftKeyFromId';
import { clearDraft } from '../local-storage/clearDraft';
import { Draft } from '../records/Draft';
import { restoreDraft } from '../local-storage/restoreDraft';
import { useDraftPurger } from './useDraftPurger';
import { DRAFT_TTL } from '../constants/draftConstants';
// @ts-expect-error module not typed
import { usePrevious } from './usePrevious';
export function useDraft({
  localStorageKeyPrefix,
  currentVersion,
  deserialize,
  serialize,
  id,
  saveTimeout = 1000,
  emptyChecker = () => false
}) {
  useDraftPurger({
    localStorageKeyPrefix,
    currentVersion,
    deserializeData: deserialize,
    emptyChecker,
    ageLimitInMs: DRAFT_TTL
  });
  const previousId = usePrevious(id);
  const draftKey = makeDraftKeyFromId(id, localStorageKeyPrefix);
  const [draft, setDraft] = useState(() => {
    return restoreDraft(draftKey, deserialize);
  });

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedSave = useCallback(debounce(dataToSave => {
    if (id && !id.includes('null')) {
      const draftToSave = new Draft({
        id,
        version: currentVersion,
        data: dataToSave
      });
      saveDraft({
        draft: draftToSave,
        serializeData: serialize,
        deserializeData: deserialize,
        localStorageKeyPrefix,
        emptyChecker,
        currentVersion
      });
      setDraft(draftToSave);
    }
  }, saveTimeout), [saveTimeout, id]);
  useEffect(() => {
    debouncedSave.flush();
    return debouncedSave.flush;
  }, [debouncedSave]);
  useEffect(() => {
    if (previousId !== id) {
      const latestDraftKey = makeDraftKeyFromId(id, localStorageKeyPrefix);
      const latestRestoredDraft = restoreDraft(latestDraftKey, deserialize);
      setDraft(latestRestoredDraft);
    }
  }, [previousId, id, setDraft, deserialize, localStorageKeyPrefix]);
  const clear = useCallback(() => {
    debouncedSave.cancel();
    clearDraft(draftKey);
    setDraft(null);
  }, [debouncedSave, draftKey]);
  const save = useCallback(dataToSave => {
    debouncedSave(dataToSave);
  }, [debouncedSave]);
  return [draft, save, clear];
}